import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "../auth/AuthContext";
import BarcodeScanInput from "../components/BarcodeScanInput";
import Pagination from "../components/Pagination";
import StockMovementPanel from "../components/StockMovementPanel";
import { formatCurrency } from "../utils/currency";
import { formatQuantity, quantityStep } from "../utils/quantity";

const PAGE_SIZE = 20;

const UNIT_OF_MEASURE_OPTIONS = [
  { value: "piece", label: "Piece" },
  { value: "kg", label: "Kilogram (kg)" },
  { value: "litre", label: "Litre" },
];

const EMPTY_FORM = {
  name: "",
  description: "",
  selling_price: "",
  cost_price: "",
  reorder_threshold: "",
  initial_quantity: "",
  unit_of_measure: "piece",
  barcode: "",
};

export default function InventoryPage() {
  const { activeShop } = useAuth();
  const canWrite = activeShop?.role === "org_admin" || activeShop?.role === "manager";

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [stockProduct, setStockProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [barcodeNotFound, setBarcodeNotFound] = useState("");
  const [attachMode, setAttachMode] = useState(false);
  const [attachSearch, setAttachSearch] = useState("");

  const shopId = activeShop?.shop_id;

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchProducts = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const res = await api.get(`/shops/${shopId}/products`);
      setProducts(res.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch products");
    }
    setLoading(false);
  }, [shopId]);

  useEffect(() => {
    setProducts([]);
    fetchProducts();
  }, [fetchProducts]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredProducts = useMemo(() => {
    let filtered = products;

    const q = filter.trim().toLowerCase();
    if (q) {
      filtered = products.filter(
        (p) =>
          String(p.id).includes(q) ||
          p.name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q)
      );
    }

    const numericFields = new Set(["id", "selling_price", "quantity_on_hand"]);
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (numericFields.has(sortField)) {
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [products, filter, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pagedProducts = useMemo(
    () => filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredProducts, currentPage]
  );

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");
    try {
      const payload = {
        name: form.name,
        description: form.description,
        selling_price: Number(form.selling_price),
        cost_price: form.cost_price === "" ? null : Number(form.cost_price),
        reorder_threshold: Number(form.reorder_threshold || 0),
        unit_of_measure: form.unit_of_measure,
        barcode: form.barcode.trim() === "" ? null : form.barcode.trim(),
      };
      if (editId) {
        await api.put(`/shops/${shopId}/products/${editId}`, payload);
        setMessage("Product updated successfully");
      } else {
        await api.post(`/shops/${shopId}/products`, {
          ...payload,
          initial_quantity: Number(form.initial_quantity || 0),
        });
        setMessage("Product created successfully");
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || "Operation failed");
    }
    setLoading(false);
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name,
      description: product.description,
      selling_price: product.selling_price,
      cost_price: product.cost_price ?? "",
      reorder_threshold: product.reorder_threshold,
      initial_quantity: "0",
      unit_of_measure: product.unit_of_measure,
      barcode: product.barcode ?? "",
    });
    setEditId(product.id);
    setMessage("");
    setError("");
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this product?");
    if (!ok) return;
    setLoading(true);
    setMessage("");
    setError("");
    try {
      await api.delete(`/shops/${shopId}/products/${id}`);
      setMessage("Product deleted successfully");
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    }
    setLoading(false);
  };

  const handleBarcodeScan = async (barcode) => {
    setBarcodeNotFound("");
    setAttachMode(false);
    setAttachSearch("");
    setError("");
    try {
      const res = await api.get(`/shops/${shopId}/products/lookup-barcode/${encodeURIComponent(barcode)}`);
      setStockProduct(res.data); // same as clicking "Stock" on the matched row
    } catch (err) {
      if (err.response?.status === 404) {
        setBarcodeNotFound(barcode);
      } else {
        setError("Barcode lookup failed");
      }
    }
  };

  const startProductFromBarcode = () => {
    setForm({ ...EMPTY_FORM, barcode: barcodeNotFound });
    setBarcodeNotFound("");
    setAttachMode(false);
    setEditId(null);
  };

  const attachMatches = useMemo(() => {
    if (!attachMode) return [];
    const q = attachSearch.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) => p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [products, attachSearch, attachMode]);

  const attachBarcodeToProduct = async (product) => {
    if (product.barcode && product.barcode !== barcodeNotFound) {
      const ok = window.confirm(
        `${product.name} already has barcode "${product.barcode}". Replace it with "${barcodeNotFound}"?`
      );
      if (!ok) return;
    }
    setError("");
    try {
      await api.put(`/shops/${shopId}/products/${product.id}`, {
        name: product.name,
        description: product.description,
        selling_price: product.selling_price,
        cost_price: product.cost_price,
        reorder_threshold: product.reorder_threshold,
        unit_of_measure: product.unit_of_measure,
        barcode: barcodeNotFound,
      });
      setMessage(`Barcode "${barcodeNotFound}" attached to ${product.name}`);
      setBarcodeNotFound("");
      setAttachMode(false);
      setAttachSearch("");
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to attach barcode");
    }
  };

  const currency = (n) => formatCurrency(n, activeShop?.currency);

  if (!activeShop) {
    return <div className="card">No shop assigned to your account yet.</div>;
  }

  if (stockProduct) {
    return (
      <StockMovementPanel
        shopId={shopId}
        product={stockProduct}
        canAdjust={canWrite}
        onClose={() => setStockProduct(null)}
        onChanged={async () => {
          await fetchProducts();
        }}
      />
    );
  }

  return (
    <>
      <div className="stats">
        <div className="chip">Total: {products.length}</div>
        <div className="search">
          <input
            type="text"
            placeholder="Search by id, name or description..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      <BarcodeScanInput onScan={handleBarcodeScan} placeholder="Scan barcode to find product..." />
      {barcodeNotFound && (
        <div className="error-msg">
          No product found for barcode "{barcodeNotFound}".
          {canWrite && (
            <>
              <button className="btn btn-secondary" style={{ marginLeft: 12 }} onClick={startProductFromBarcode}>
                Create new product with this barcode
              </button>
              <button
                className="btn btn-secondary"
                style={{ marginLeft: 8 }}
                onClick={() => setAttachMode(true)}
              >
                Attach to existing product
              </button>
            </>
          )}
          {attachMode && (
            <div className="barcode-attach-search">
              <input
                type="text"
                placeholder="Search existing products by name..."
                value={attachSearch}
                onChange={(e) => setAttachSearch(e.target.value)}
                autoFocus
              />
              {attachSearch.trim() !== "" && (
                <div className="attach-matches">
                  {attachMatches.length === 0 && <div className="empty">No matching products.</div>}
                  {attachMatches.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => attachBarcodeToProduct(p)}
                    >
                      {p.name}
                      {p.barcode ? ` (currently has barcode ${p.barcode})` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="content-grid">
        {canWrite && (
          <div className="card form-card">
            <h2>{editId ? "Edit Product" : "Add Product"}</h2>
            <form onSubmit={handleSubmit} className="product-form labeled-form">
              <label className="field-label">
                Name
                <input type="text" name="name" value={form.name} onChange={handleChange} required />
              </label>
              <label className="field-label">
                Description
                <input type="text" name="description" value={form.description} onChange={handleChange} required />
              </label>
              <label className="field-label">
                Selling price
                <input
                  type="number"
                  name="selling_price"
                  value={form.selling_price}
                  onChange={handleChange}
                  required
                  step="0.01"
                />
              </label>
              <label className="field-label">
                Cost price (optional)
                <input type="number" name="cost_price" value={form.cost_price} onChange={handleChange} step="0.01" />
              </label>
              <label className="field-label">
                Barcode (optional)
                <input type="text" name="barcode" value={form.barcode} onChange={handleChange} />
              </label>
              <label className="field-label">
                Unit of measure
                <select name="unit_of_measure" value={form.unit_of_measure} onChange={handleChange}>
                  {UNIT_OF_MEASURE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Reorder at
                <input
                  type="number"
                  name="reorder_threshold"
                  value={form.reorder_threshold}
                  onChange={handleChange}
                  step={quantityStep(form.unit_of_measure)}
                />
              </label>
              {!editId && (
                <label className="field-label">
                  Initial stock
                  <input
                    type="number"
                    name="initial_quantity"
                    value={form.initial_quantity}
                    onChange={handleChange}
                    step={quantityStep(form.unit_of_measure)}
                  />
                </label>
              )}
              <div className="form-actions">
                <button className="btn" type="submit" disabled={loading}>
                  {editId ? "Update" : "Add"}
                </button>
                {editId && (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => {
                      resetForm();
                      setMessage("");
                      setError("");
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
            {message && <div className="success-msg">{message}</div>}
            {error && <div className="error-msg">{error}</div>}
          </div>
        )}

        <div className="card list-card">
          <h2>Products</h2>
          {!canWrite && (message || error) && (
            <>
              {message && <div className="success-msg">{message}</div>}
              {error && <div className="error-msg">{error}</div>}
            </>
          )}
          {loading ? (
            <div className="loader">Loading...</div>
          ) : (
            <>
            <div className="scroll-x">
              <table className="product-table">
                <thead>
                  <tr>
                    <th
                      className={`sortable ${sortField === "name" ? `sort-${sortDirection}` : ""}`}
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </th>
                    <th>Description</th>
                    <th
                      className={`sortable ${sortField === "selling_price" ? `sort-${sortDirection}` : ""}`}
                      onClick={() => handleSort("selling_price")}
                    >
                      Price
                    </th>
                    <th
                      className={`sortable ${sortField === "quantity_on_hand" ? `sort-${sortDirection}` : ""}`}
                      onClick={() => handleSort("quantity_on_hand")}
                    >
                      Stock
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedProducts.map((p) => {
                    const lowStock = p.quantity_on_hand <= p.reorder_threshold;
                    return (
                      <tr key={p.id}>
                        <td className="name-cell">{p.name}</td>
                        <td className="desc-cell" title={p.description}>
                          {p.description}
                        </td>
                        <td className="price-cell">{currency(p.selling_price)}</td>
                        <td>
                          <span className={`qty-badge ${lowStock ? "qty-low" : ""}`} title={lowStock ? "At or below reorder threshold" : ""}>
                            {formatQuantity(p.quantity_on_hand, p.unit_of_measure)}
                          </span>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button className="btn btn-edit" onClick={() => setStockProduct(p)}>
                              Stock
                            </button>
                            {canWrite && (
                              <>
                                <button className="btn btn-edit" onClick={() => handleEdit(p)}>
                                  Edit
                                </button>
                                <button className="btn btn-delete" onClick={() => handleDelete(p.id)}>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">
                        No products found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
